import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrderBumpService,
  OrderBumpsResponse,
} from '@/services/order-bumps.ts';
import { useTranslation } from 'react-i18next';
import useModalStore from '@/store/useModalStore';
import { toast } from '@/hooks/use-toast';

interface UseOrderBumpsProps {
  productId: string;
}

export interface OrderBump {
  id: string
  display_text: string
  display_description: string
  call_to_action: string
  product: {
    id: string
    name: string
    description: string
    email_support: string
    whatsapp_support: string
    product_delivery: string
    member_area_access: string
    sales_page: string
    status: string
    relationship: {
      role: string
    }
    secret_order_bump: boolean
    category: {
      id: string
      name: string
      type: string
      label: string
    }
  }
  offer: {
    id: string
    product_id: string
    recurrence?: string;
    product: {
      id: string
      name: string
      description: string
      email_support: string
      whatsapp_support: string
      product_delivery: string
      member_area_access: string
      sales_page: string
      status: string
      relationship: {
        role: string
      }
      secret_order_bump: boolean
      category: {
        id: string
        name: string
        type: string
        label: string
      }
    }
    name: string
    is_affiliate_enabled: boolean
    price: string
    price_formatted: string
    currency: string
    sales_page: string
    payment_type: string
    recurring_interval: any
  }
}


export interface CreateOrderBumpData {
  productId: string;
  offer_id: string;
  display_text: string;
  display_description: string;
  call_to_action: string;
}
export interface ActiveSecretOrderBumpData {
  secret_order_bump: boolean;
}

interface UpdateOrderBumpData extends Partial<CreateOrderBumpData> {
  status?: 'active' | 'inactive';
}

interface UseOrderBumpsReturn {
  orderBumps: OrderBumpsResponse | undefined;
  isLoading: boolean;
  refetch,
  createOrderBump: (data: CreateOrderBumpData) => Promise<any>;
  updateOrderBump: (data: {
    orderBumpId: string;
    data: UpdateOrderBumpData;
  }) => Promise<any>;
  deleteOrderBump: (orderBumpId: string) => void;
  activateSecretOrderBump: (data: ActiveSecretOrderBumpData) => Promise<any>;
  reorderOrderBumps: (params: {
    oldIndex: number;
    newIndex: number;
  }) => Promise<void>;
  toggleOrderBumpStatus: (
    orderBumpId: string,
    currentStatus: 'active' | 'inactive'
  ) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export function useOrderBumps({
  productId,
}: UseOrderBumpsProps): UseOrderBumpsReturn {
  const queryClient = useQueryClient();
  const { t } = useTranslation('orderBumps');
  const { closeModal } = useModalStore();
  const { data: orderBumps, isLoading, refetch } = useQuery({
    queryKey: ['orderBumps', productId],
    queryFn: () => OrderBumpService.listOrderBumps(productId),
  });

  const createOrderBumpMutation = useMutation({
    mutationFn: (data: CreateOrderBumpData) =>
      OrderBumpService.createOrderBump({ productId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBumps', productId] });
      toast({
        description: t('order_bump_created_description'),
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        description: t('order_bump_create_error_description'),
        variant: 'error',
      });
    },
  });

  const updateOrderBumpMutation = useMutation({
    mutationFn: ({
      orderBumpId,
      data,
    }: {
      orderBumpId: string;
      data: UpdateOrderBumpData;
    }) => OrderBumpService.updateOrderBump({ productId, orderBumpId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBumps', productId] });
      toast({
        description: t('order_bump_updated_description'),
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        description: t('order_bump_update_error_description'),
        variant: 'error',
      });
    },
  });

  const deleteOrderBumpMutation = useMutation({
    mutationFn: (orderBumpId: string) =>
      OrderBumpService.deleteOrderBump({ productId, orderBumpId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderBumps', productId] });
      toast({
        description: t('order_bump_deleted_description'),
        variant: 'success',
      });
      closeModal('delete-orderBump');
    },
    onError: () => {
      toast({
        description: t('order_bump_delete_error_description'),
        variant: 'error',
      });
    },
  });

  const reorderOrderBumps = async ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const newOrderBumps = [...orderBumps?.data];
    const [removed] = newOrderBumps.splice(oldIndex, 1);
    newOrderBumps.splice(newIndex, 0, removed);

    const updatedOrderBumps = newOrderBumps.map((bump, index) => ({
      ...bump,
      position: index + 1,
    }));

    queryClient.setQueryData(['orderBumps', productId], updatedOrderBumps);

    try {
      await OrderBumpService.reorderOrderBumps({
        productId,
        orderBumpIds: updatedOrderBumps.map((bump) => bump.product.name),
      });
    } catch (error) {
      queryClient.invalidateQueries({ queryKey: ['orderBumps', productId] });
      toast({
        description: t('order_bump_reorder_error_description'),
        variant: 'error',
      });
    }
  };

  const toggleOrderBumpStatus = async (
    orderBumpId: string,
    currentStatus: 'active' | 'inactive'
  ) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      await updateOrderBumpMutation.mutateAsync({
        orderBumpId,
        data: { status: newStatus },
      });
    } catch (error) {
      console.error('Error toggling order bump status:', error);
    }
  };

  const activateSecretOrderBump = async (data: ActiveSecretOrderBumpData) => {
    try {
      const response = await OrderBumpService.activateScretOrderBump(
        productId,
        data
      );
      toast({
        description: data?.secret_order_bump
          ? t('secret_order_bump_enabled')
          : t('secret_order_bump_disabled'),
        variant: 'success',
      });
      return response;
    } catch (error) {
      console.error('Error activating secret order bump:', error);
      toast({
        description: t('secret_order_bump_update_error'),
        variant: 'error',
      });
      throw error;
    }
  };

  return {
    orderBumps,
    isLoading,
    refetch,
    createOrderBump: createOrderBumpMutation.mutateAsync,
    updateOrderBump: updateOrderBumpMutation.mutateAsync,
    deleteOrderBump: deleteOrderBumpMutation.mutate,
    activateSecretOrderBump,
    reorderOrderBumps,
    toggleOrderBumpStatus,
    isCreating: createOrderBumpMutation.isPending,
    isUpdating: updateOrderBumpMutation.isPending,
    isDeleting: deleteOrderBumpMutation.isPending,
  };
}
